import { CODE_LENGTH, isValidPeg, parsePeg, pegClasses, formatResult, explainResult } from './pegs.js';
import { mergeRows, lastLineNumber, rowMode, reactionsFor, hasReaction } from './rows.js';
import { faceId, faceList } from './faces.js';
import { hasSeen, markSeen } from './storage.js';
import { loadDraft, saveDraft, dropDraft } from './draft.js';
import { scoreTap } from './haptics.js';
import { createHold } from './hold.js';
import { createTap } from './tap.js';
import { createClock } from './clock.js';

// Which of the three a row is: yours, theirs, or a cracked code. It goes on the
// box holding the row, its score and its explanation, which is the one place
// the state is needed - everything drawn in the row's colour reads it from the
// --accent that class declares.
//
// Spelled out, not assembled: Tailwind drops any class it cannot see written
// down literally.
const GROUP_CLASSES = {
    on: 'peg-group--on',
    theirs: 'peg-group--theirs',
    gold: 'peg-group--gold',
};

/** The id a score having been spelled out is recorded under. */
export const SCORING_TUTORIAL = 'scoring';

/** The id the line offering the hold that pins a peg is recorded under. */
export const PINNING_TUTORIAL = 'pinning';

/**
 * The board's state machine, kept free of the DOM so it can be tested directly.
 *
 * Your POST response and the snapshot listener both append rows, in either
 * order and possibly twice, so both funnel through mergeRows.
 */
export function createBoard({
    gameId,
    me,
    rows = [],
    turn = '',
    gameOver = false,
    won = false,
    palette = [],
    // game.MatchKind: what this mode scores a peg on. Four's answer is the
    // fallback, as ParseMode's fallback is Four.
    matches = 'color',
    chat = {},
    reactions = [],
    solo = false,
    awaitingOpponent = false,
    opponentName = '',
    shareUrl = '',
    // Where a move is posted. A game posts to its own ID; the daily posts to
    // the day it is. The board plays either without knowing which it is on.
    guessUrl = gameId ? `/g/${gameId}/guess` : '',
    forfeitUrl = gameId ? `/g/${gameId}/forfeit` : '',
    // What this board's half-built guess is filed under. Not gameId: that is
    // what gates the Firestore listener, and the daily has no game to listen
    // to - it files its draft under the day it is instead.
    draftKey = '',
    // The board's opening lines, or null to let it speak for itself. An empty
    // list is a board whose page has already introduced it.
    premise = null,
    // How this player's go at today's code stands, or null on a game board.
    daily = null,
    // Whether to ask this player what they are called before the link goes
    // out, and what they are called now.
    askName = false,
    name = '',
    fetchImpl = null,
    nav = null,
    // Injected so the clock can be driven without waiting out real seconds.
    now = () => Date.now(),
    onSurge = null,
    onNewRow = null,
    onExplain = null,
    onCelebrate = null,
    storage = null,
    cue = () => {},
} = {}) {
    // A peg is pinned from three places and released from two, all with the
    // same press held down, so one of these serves the lot.
    const hold = createHold();

    // A tap on a played row's pegs explains the row, and two of them copy it,
    // so the first has to wait for the second not to come.
    const tap = createTap();

    // The guess this player was part-way through, which belongs to a game that
    // is still being played. A board that is over forgets its draft here rather
    // than leaving it to age out: the game it was working state for is done.
    if (gameOver) dropDraft(storage, draftKey);
    const draft = gameOver ? null : loadDraft(storage, draftKey, palette);

    // What the day is timed by, filed under the same key as the draft - both
    // are this player's working state on one board, and both are done with when
    // it is. A game board gets a clock with nowhere to keep anything, which
    // counts and is never asked: only the daily is played against time.
    const clock = createClock({ storage, key: daily ? draftKey : '', now });
    if (gameOver) clock.clear();

    /**
     * The clock, as the move that carries it says it: a field on a daily's
     * request and nothing at all on a game's, which is not played against time
     * and has no business sending one.
     *
     * Every row carries it, because the board cannot know which row will turn
     * out to be the last one. The server spends it only on the row that ends
     * the attempt. Banking as it goes is what makes a reload lose the last few
     * seconds rather than the whole morning.
     */
    const timed = () => {
        if (!daily) return {};
        clock.save();
        return { elapsed_ms: clock.elapsed() };
    };

    const board = {
        gameId,
        me,
        rows: mergeRows([], rows),
        turn,
        gameOver,
        won,
        palette,
        matches,
        chat: chat || {},
        // A board with nobody to react to is sent no list at all.
        reactions: reactions || [],
        solo,
        /** Whether the second seat is still empty. */
        awaitingOpponent,
        opponentName,
        shareUrl,
        guessUrl,
        forfeitUrl,
        premise,
        daily,
        /** Whether the ask is still standing. */
        askName,
        name,
        /** The name being typed, kept apart until it is sent. */
        nameDraft: name,
        /** The save in flight, so the field cannot be sent twice. */
        namePending: false,
        /** Whether the copy button is showing its confirmation. */
        copied: false,
        /** Injected in tests; the real page shares through the browser's own. */
        nav,
        storage,
        /** Where the board is felt. A no-op off a phone, and on every iPhone. */
        cue,

        /** The line number whose score is currently spelled out, 0 for none. */
        explaining: 0,
        /** Whether that explanation opened itself rather than being asked for. */
        autoExplained: false,
        /** Whether this player has ever been shown what a score means. */
        scoringSeen: hasSeen(storage, SCORING_TUTORIAL),
        /** Whether this player has ever been told that a peg can be pinned. */
        pinningSeen: hasSeen(storage, PINNING_TUTORIAL),

        /**
         * The peg pinned to each socket, '' where nothing is.
         *
         * A pin is this player's own note to themselves about where a peg
         * belongs - the server neither knows nor cares, and the opponent's
         * board is untouched by it. It survives a reload, because a note that
         * did not would not be a note; where it survives, and why that costs no
         * banner, is draft.js.
         */
        locks: draft ? draft.locks : Array(CODE_LENGTH).fill(''),

        guess: draft ? draft.guess : Array(CODE_LENGTH).fill(''),
        active: 0,
        submitting: false,
        reacting: false,
        /** Whether the six are out on the rule, waiting to be tapped. */
        saying: false,
        error: '',
        /**
         * Whether this player has tapped a send the turn cannot answer.
         *
         * A flag rather than the words themselves, so the words are derived and
         * cannot outlive what they are about - see hint.
         */
        hintAsked: false,
        /** Where the surge is handed to the DOM. Null outside a browser. */
        onSurge,
        onNewRow,
        onExplain,
        /** Where the win is handed to the DOM. Null outside a browser. */
        onCelebrate,

        // Rows at or below this were already here on load, so they do not
        // strike. Reloading a long game must not set every row flickering.
        loadedThrough: lastLineNumber(mergeRows([], rows)),

        paletteColor: parsePeg(palette[0] || '').color,

        /** Whether it is this player's move. */
        get myTurn() {
            return !this.gameOver && this.turn === this.me;
        },

        /**
         * Whether the guess row can be edited.
         *
         * Deliberately not myTurn: a player may build their next guess while
         * the opponent is thinking, which is most of the time a turn-based game
         * spends. Only sending is a move, so only sending waits for the turn.
         */
        get canCompose() {
            return !this.gameOver;
        },

        /** Whether all five sockets hold a peg, whoever's turn it is. */
        get filled() {
            return this.guess.every((code) => isValidPeg(code));
        },

        /** What the header calls the other side of the board. */
        get opponentLabel() {
            if (this.solo) return 'Solo';
            if (this.awaitingOpponent) return 'waiting…';
            // Just the name: the lamp beside it in their accent is what says
            // this is the opponent, so the word would only say it twice.
            return this.opponentName;
        },

        /**
         * Who the rule is about: the opponent while their turn runs, and the
         * winner once there is one.
         *
         * Its own value rather than part of the label, because it is the one
         * word on that line drawn in their colour - and because the cases with
         * nobody to name, this player's own win among them, leave the label a
         * whole sentence instead.
         */
        get ruleName() {
            if (this.solo || this.awaitingOpponent) return '';
            if (this.gameOver) return this.won ? '' : this.opponentName;
            // This player's own turn names nobody: the line says "your turn",
            // and the white it is lit in is already whose it is.
            if (this.myTurn) return '';
            // The possessive travels with the name so that the two are one word
            // in their colour, and so the gap between the spans falls after the
            // apostrophe rather than before it.
            return `${this.opponentName}\u2019s`;
        },

        /**
         * Why the guess area is inert, when it is - and who took the game,
         * once nothing is waiting on anybody.
         *
         * What follows the name, when there is one. The rule carries this, so
         * it is what a name can be said about rather than a sentence of its
         * own: the line is already the shape of "somebody, doing something".
         */
        get ruleLabel() {
            if (this.gameOver) {
                if (this.won) return 'You won';
                return this.solo ? 'Game over' : 'won';
            }
            if (this.solo) return 'Waiting for your move…';
            if (this.awaitingOpponent) return 'Waiting for someone to join…';
            // The two live states in one grammar, so that the word that differs
            // is the only thing to read. Said on this player's turn as well as
            // on the other one: a board that only speaks while it is blocked
            // leaves silence to mean "go", which is the one thing a player who
            // has never seen this game cannot know it means.
            return this.myTurn ? 'Your turn' : 'turn';
        },

        /**
         * Whether the rule says anything at all.
         *
         * Silent while the six are out on it - the label and the lamps cannot
         * both have the line - and silent on a solo board's own turn, where
         * there is no second player for "yours" to be against and the lit rule
         * is the whole of what there is to say.
         */
        get ruleShown() {
            if (this.saying) return false;
            return !(this.myTurn && this.solo);
        },

        /**
         * Whether the opponent's lamp in the header is burning.
         *
         * Only while they are the one thinking. A game that has ended is
         * nobody's move, so the lamp goes down with the turn rather than
         * staying lit over a board that has stopped.
         */
        get lampLit() {
            return !this.gameOver && !this.myTurn;
        },

        /**
         * Which way the rule is lit, in the two colours the board already
         * scores rows in.
         *
         * A live board is lit for whoever it waits on; a finished one for
         * whoever won, which is the same signal answering the same question a
         * move later. Neither, while the seat is empty or a solo board has
         * simply run out - there is nobody the light would be about.
         */
        get ruleMine() {
            if (this.gameOver) return this.won;
            return !this.awaitingOpponent && this.myTurn;
        },

        get ruleTheirs() {
            if (this.gameOver) return !this.solo && !this.won;
            return !this.awaitingOpponent && !this.myTurn;
        },

        /**
         * Whether the rows carry the opponent's colour behind them.
         *
         * The same question the rule is lit for, with the ending taken out: a
         * finished board is lit for whoever won, and a board that is over is
         * waiting on nobody. Solo boards and the daily fall out of it on their
         * own - their turn is always this player's.
         */
        get waitingOnThem() {
            return !this.gameOver && this.ruleTheirs;
        },

        /** The navigator to share through, injectable so this can be tested. */
        get navigator() {
            return this.nav || globalThis.navigator || null;
        },

        /** Whether a native share sheet is available. */
        get canShare() {
            const n = this.navigator;
            return Boolean(this.shareUrl && n && typeof n.share === 'function');
        },

        /** The message the link travels with, so it arrives as an invitation. */
        get shareText() {
            return 'Break this code with me.';
        },

        /** Opens the native share sheet. A dismissed sheet is not an error. */
        async share() {
            if (!this.canShare) return false;
            try {
                await this.navigator.share({
                    title: 'unkoad',
                    text: this.shareText,
                    url: this.shareUrl,
                });
                return true;
            } catch {
                // Cancelling the sheet rejects, and is not worth reporting.
                return false;
            }
        },

        /** Copies the link, confirming briefly on the button itself. */
        async copy() {
            const n = this.navigator;
            if (!this.shareUrl || !n || !n.clipboard) return false;
            try {
                await n.clipboard.writeText(this.shareUrl);
                this.copied = true;
                this.cue('copied');
                setTimeout(() => { this.copied = false; }, 1500);
                return true;
            } catch {
                return false;
            }
        },

        /*
         * What you are called, asked once, at the moment it matters: the link
         * is about to reach somebody who will see nothing else of you.
         *
         * Skippable, because the codename a player was dealt is a real answer
         * - and asked here rather than on the way in, where it would be a form
         * standing between somebody and their first game.
         */

        /**
         * Keeps the codename this player was dealt.
         *
         * Sent, not just hidden: an answer that lived only in this tab would be
         * asked again on the next link, which is the one thing the ask promises
         * not to do. The panel goes at once and the write follows - a keep that
         * failed costs one more ask, never a wait.
         */
        skipName() {
            this.askName = false;
            return this.postName(this.name);
        },

        /**
         * Sends the typed name, then puts the ask away.
         *
         * The server's answer is authoritative: it trims and truncates what it
         * was given, and it is what an opponent will actually read.
         */
        async saveName() {
            if (this.namePending) return false;

            // An empty field, or the name already held, is a keep.
            const typed = (this.nameDraft || '').trim();
            if (!typed || typed === this.name) return this.skipName();

            const saved = await this.postName(typed);
            // A name that was not stored is still worth typing, so the field
            // stays as it is and the same button is what fixes it.
            if (!saved) return false;

            this.askName = false;
            this.cue('peg');
            return true;
        },

        /**
         * Stores a name and settles the ask, whichever button sent it.
         *
         * Both answers are worth a write: the one the server keeps is what an
         * opponent reads, and either marks this player as having been asked.
         */
        async postName(name) {
            if (this.namePending || !name) return false;

            const doFetch = fetchImpl || globalThis.fetch;
            this.namePending = true;
            try {
                const res = await doFetch('/name', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({ name }).toString(),
                });
                if (!res.ok) return false;

                const body = await res.json();
                if (body && body.name) this.name = body.name;
                return true;
            } catch {
                // Nothing was stored; what each caller does about that is
                // theirs to decide.
                return false;
            } finally {
                this.namePending = false;
            }
        },

        /** Whether there is a finished day worth passing on. */
        get canShareResult() {
            return Boolean(this.daily && this.daily.share);
        },

        /**
         * Passes the day on: the native sheet where there is one, the
         * clipboard where there is not.
         *
         * The text is a grid of scores, a row count and the sharer's personal
         * link, none of which says anything about the code - which is the
         * whole point, because it can be pasted in front of somebody who has
         * not played today.
         *
         * The link is part of that text rather than the sheet's own url
         * field. Given both, share targets routinely keep one and drop the
         * other, and the clipboard fallback below has no url field at all -
         * one string is the only shape that survives every route out.
         */
        async shareResult() {
            if (!this.canShareResult) return false;

            const n = this.navigator;
            const text = this.daily.share;

            if (n && typeof n.share === 'function') {
                try {
                    await n.share({ title: 'unkoad', text });
                    return true;
                } catch {
                    // Cancelling the sheet rejects, and is not worth reporting.
                    return false;
                }
            }

            if (!n || !n.clipboard) return false;
            try {
                await n.clipboard.writeText(text);
                this.copied = true;
                this.cue('copied');
                setTimeout(() => { this.copied = false; }, 1500);
                return true;
            } catch {
                return false;
            }
        },

        /** Whether the guess row is complete and can be sent. */
        get ready() {
            return this.myTurn && !this.submitting && this.filled;
        },

        /**
         * Whether a guess of this player's is on its way to the server.
         *
         * Narrower than submitting, which is also true while a game is being
         * given up: what this is for is the row being sent, and a forfeit has
         * no row. The pegs are still in their sockets throughout - the row is
         * not refilled until the answer lands - so filled is what tells the two
         * apart.
         */
        get sending() {
            return this.submitting && this.filled;
        },

        /**
         * Whether the guess row's circuit is closed.
         *
         * Not ready, which goes false the instant a send starts: a guess in
         * flight is the most closed the circuit ever is, and a wire that went
         * dark at the tap would be telling the player the opposite of what is
         * happening at exactly the moment they are least sure the tap landed.
         * So the wire is lit for a row that can be sent and for one that is
         * being sent, and only the beat on it tells those apart.
         */
        get lit() {
            return this.ready || this.sending;
        },

        /**
         * Whether a finished row is waiting on the turn.
         *
         * canCompose lets a player build their guess while the opponent
         * thinks, so this is the common case rather than an edge one - and
         * without it the button says the same thing about a row with two pegs
         * in it as about one that is ready and only waiting.
         */
        get charged() {
            return this.filled && !this.myTurn && !this.gameOver;
        },

        /**
         * The answer to a send the turn cannot take yet.
         *
         * Derived rather than stored, so it goes on its own the moment it stops
         * being true - when the turn lands, or when a peg comes back out of the
         * row it was about. Nothing has to remember to clear it.
         *
         * It promises no more than the board does: the row is not sent for the
         * player when their turn comes, so it says to tap again rather than
         * that it will go on its own.
         *
         * It names nobody: whose turn it is is on the rule a line above, in
         * their own colour, and the header lamp is saying it a third time.
         * Repeating it here is how a board ends up with nothing being read.
         */
        get hint() {
            if (!this.hintAsked || !this.charged) return '';
            return 'Not yet \u2014 tap again on your turn.';
        },

        /**
         * Places a peg in the active socket and advances to the next empty one.
         *
         * The peg the socket already holds empties it instead. Emptying has to
         * live somewhere, and the palette is where the hand already is: it
         * leaves the socket itself free to answer a tap by taking the cursor,
         * so a peg a player went to look at is never lost to the looking.
         *
         * A pinned socket is the one that keeps its peg. The note says this peg
         * belongs here, and the hold that wrote it is what takes it back - a
         * pin the palette could undo with a tap would not be worth writing.
         */
        pick(code) {
            if (!this.canCompose || !isValidPeg(code)) return;

            if (this.guess[this.active] === code) {
                if (!this.isPinned(this.active)) this.clear(this.active);
                return;
            }

            this.guess[this.active] = code;
            this.settle();
            this.active = this.nextEmpty(this.active);
            this.cue(this.filled ? 'full' : 'peg');
        },

        /**
         * Selects a socket to fill next, which is the whole of what a tap on a
         * socket does. A full socket answers it like an empty one: the tap says
         * where the next peg goes, and says nothing about the peg already there.
         */
        focus(index) {
            if (!this.canCompose) return;
            if (index < 0 || index >= CODE_LENGTH) return;
            this.active = index;
        },

        /** Empties a socket and puts the cursor back on it. */
        clear(index) {
            if (!this.canCompose) return;
            if (index < 0 || index >= CODE_LENGTH) return;
            this.guess[index] = '';
            this.settle();
            this.active = index;
            this.cue('clear');
        },

        /**
         * Loads a played row back into the guess row, so a near miss can be
         * adjusted rather than tapped out again from nothing.
         *
         * Either player's row will do - both are guessing at the same code.
         * What is refused is a row built from pegs this mode does not offer:
         * the forfeit row is drawn from outside the alphabet on purpose, and a
         * guess made of it is one the server would only reject.
         */
        copyRow(row) {
            // The tap under this double is still waiting to explain the row.
            // The double is what says it was never a tap on its own - and it
            // says so whether or not this row can be copied at all.
            tap.cancel();

            if (!this.canCompose) return false;

            const codes = row && row.codes;
            if (!Array.isArray(codes) || codes.length !== CODE_LENGTH) return false;
            if (!codes.every((code) => this.palette.includes(code))) return false;

            this.guess = [...codes];
            this.settle();
            this.active = 0;
            return true;
        },

        /**
         * The next socket needing a peg, wrapping around so filling the last
         * one lands on whatever is still empty rather than sticking at the end.
         */
        nextEmpty(from) {
            for (let i = from + 1; i < CODE_LENGTH; i++) {
                if (!this.guess[i]) return i;
            }
            for (let i = 0; i <= from; i++) {
                if (!this.guess[i]) return i;
            }
            return from;
        },

        /** The first socket still wanting a peg, or 0 when the row is full. */
        get firstEmpty() {
            const i = this.guess.findIndex((code) => !code);
            return i === -1 ? 0 : i;
        },

        /*
         * Pinning a peg.
         *
         * A pin is a note saying which peg belongs in a socket, and its only
         * effect is that sending a guess refills the row from the notes instead
         * of emptying it. It is mostly soft: a pinned socket takes the cursor
         * and takes a new colour exactly like any other, because a socket that
         * refused input would be a second kind of socket to learn. The one
         * thing it resists is being emptied by a tap on the peg it holds, which
         * is what the hold is for - and holding is also what put it there, so
         * the player who wants out has nothing new to remember.
         *
         * That leaves one rule, applied after every edit rather than handled
         * case by case: a note survives only while its socket still holds the
         * peg it names. Clearing the socket drops it, picking another colour
         * drops it, picking the same colour keeps it, and copying a played row
         * keeps exactly the notes that row happens to agree with.
         */

        /**
         * Whether a note is attached to this socket.
         *
         * A note is about the next guess, so a board with no next guess has
         * none. The row stays on screen when the game ends and the ring would
         * stay lit on it, still promising to refill a row nothing will empty.
         */
        isPinned(index) {
            return this.canCompose && Boolean(this.locks[index]);
        },

        /**
         * Drops the notes that no longer describe their socket, and files what
         * is left.
         *
         * The one place the rule above lives, and so the one place worth
         * filing from: everything that writes to the guess row calls this
         * instead of reasoning about pins itself, which makes it the single
         * point where the row has finished changing.
         */
        settle() {
            for (let i = 0; i < CODE_LENGTH; i++) {
                if (this.locks[i] && this.guess[i] !== this.locks[i]) this.locks[i] = '';
            }
            this.keepDraft();
        },

        /**
         * Files the row as it stands, so it is still there on the way back.
         *
         * Only while it can still be edited: a draft is working state for a
         * game being played, and a board that is over has nothing to work on.
         */
        keepDraft() {
            if (!this.canCompose) return;
            saveDraft(this.storage, draftKey, { guess: this.guess, locks: this.locks });
        },

        /**
         * Empties the guess row for good, which is what the game ending does.
         *
         * The row stays on screen when the board finishes - the wire runs
         * through it and the board would lose its bottom edge without it - so
         * pegs left standing in it are a guess still being built, offered to a
         * player who can no longer send one. Emptying says the true thing in
         * the place they were working.
         *
         * The pins go with the pegs: a note about the next guess has nothing
         * left to be about. So does the draft, rather than waiting to be aged
         * out - the game it was working state for is done.
         */
        retireGuess() {
            this.guess = Array(CODE_LENGTH).fill('');
            this.locks = Array(CODE_LENGTH).fill('');
            this.active = 0;
            dropDraft(this.storage, draftKey);
        },

        /**
         * Pins a peg to a socket, or takes the pin out if that peg is already
         * the one pinned there.
         *
         * Holding the same peg twice releasing it is what makes the gesture
         * explorable: whatever a hold did, holding again undoes it, and the
         * player never has to find a different control to get back.
         */
        pin(index, code) {
            if (!this.canCompose) return false;
            if (index < 0 || index >= CODE_LENGTH) return false;
            // The forfeit row is drawn from outside the alphabet on purpose,
            // and a peg this mode does not offer is not one to build a guess
            // from - the same guard copyRow uses.
            if (!this.palette.includes(code)) return false;

            if (this.locks[index] === code) return this.unpin(index);

            this.locks[index] = code;
            this.guess[index] = code;
            this.settle();
            if (this.active === index) this.active = this.nextEmpty(index);

            this.rememberPinning();
            this.cue('hold');
            return true;
        },

        /** Takes the pin out and empties the socket with it. */
        unpin(index) {
            if (!this.canCompose) return false;
            if (!this.isPinned(index)) return false;

            this.locks[index] = '';
            this.guess[index] = '';
            this.settle();
            this.active = index;
            this.cue('clear');
            return true;
        },

        /**
         * A hold on a socket in the guess row, which toggles that socket's note.
         *
         * An empty socket answers nothing: there is no peg to pin and no note to
         * take out, and inventing an answer for it would make the gesture mean
         * two different things depending on what the socket happened to hold.
         */
        holdSlot(index) {
            if (index < 0 || index >= CODE_LENGTH) return false;
            if (this.isPinned(index)) return this.unpin(index);
            return this.pin(index, this.guess[index]);
        },

        /** A hold on a palette peg, which pins the socket the cursor is on. */
        holdPeg(code) {
            return this.pin(this.active, code);
        },

        /** Whether the board still owes this player the line about holding. */
        get showPinHint() {
            return !this.pinningSeen && !this.gameOver && this.rows.length > 0;
        },

        rememberPinning() {
            if (this.pinningSeen) return;
            this.pinningSeen = true;
            markSeen(this.storage, PINNING_TUTORIAL);
        },

        /*
         * The press held down, shared by all three places a peg can be pinned
         * from. One hold rather than one each: only one finger is ever down,
         * and a second timer could only ever disagree with the first.
         */

        /** Starts a hold. Returning false leaves the click that follows alone. */
        startHold(action) {
            hold.start(action);
        },

        /** Ends a hold early: a tap, a scroll, a finger leaving the peg. */
        cancelHold() {
            hold.cancel();
        },

        /**
         * The press arriving as a menu, from the right button or from the phone
         * browser that raises one on its own partway through a long press.
         * Answered once between the two - see hold.menu.
         */
        holdMenu(action) {
            return hold.menu(action);
        },

        /**
         * Swallows the click ending a completed hold, and says it has, so a
         * socket that was just pinned does not also answer the tap.
         */
        holdClick(event) {
            return hold.click(event);
        },

        /** Folds an update in, from either the POST response or the listener. */
        ingest(update = {}) {
            // Read before anything moves: the flourishes below fire on the turn
            // and on the win arriving, which is only knowable by comparing the
            // two states.
            const wasMine = this.myTurn;
            const hadWon = this.won;
            const hadOver = this.gameOver;
            const throughLine = lastLineNumber(this.rows);
            const saidBefore = theirReactions(this.chat, this.me);

            if (Array.isArray(update.rows)) {
                this.rows = mergeRows(this.rows, update.rows);
            }
            if (typeof update.turn === 'string') this.turn = update.turn;
            if (typeof update.game_over === 'boolean') this.gameOver = update.game_over;
            if (typeof update.won === 'boolean') this.won = update.won;
            if (typeof update.awaiting_opponent === 'boolean') {
                this.awaitingOpponent = update.awaiting_opponent;
                if (!update.awaiting_opponent) this.shareUrl = '';
            }
            if (typeof update.opponent_name === 'string' && update.opponent_name) {
                this.opponentName = update.opponent_name;
            }
            // Replaced, not merged: both senders supply the map as it stands,
            // and a reaction string is rewritten in place as it grows.
            if (update.chat && typeof update.chat === 'object') this.chat = update.chat;
            // The daily's standing, which only the response to a daily guess
            // carries. It is the finish panel's whole content, and the reason
            // cracking the code does not need a reload to be told about.
            if (update.daily && typeof update.daily === 'object') this.daily = update.daily;

            // The game ending is what the half-built guess was for ending, so
            // the row goes with it - see retireGuess. The clock goes the same
            // way, and for the same reason - its total has already travelled
            // with the row that ended the attempt.
            if (!hadOver && this.gameOver) {
                this.retireGuess();
                clock.stop();
            }

            if (!wasMine && this.myTurn && this.filled) this.surge();
            if (!hadWon && this.won) this.celebrate();

            // One tap per update, the biggest news first. Two would collide in
            // the motor anyway, and which one survived would be an accident.
            //
            // The turn is felt whether or not a guess was waiting, unlike the
            // surge above: light needs a row to arrive in, but "it is your move"
            // is worth saying to a player holding an empty one.
            const landed = this.rows.filter((row) => row.line_number > throughLine);
            const scored = landed.filter((row) => row.user === this.me && !row.forfeit).pop();

            if (!hadWon && this.won) this.cue('win');
            else if (!hadOver && this.gameOver) this.cue('lost');
            else if (scored) this.cue(scoreTap(scored.result));
            else if (!wasMine && this.myTurn) this.cue('turn');
            else if (theirReactions(this.chat, this.me) !== saidBefore) this.cue('poke');

            // A row landing takes the wire back. The rule is needed to say
            // whose turn it now is, and a reaction sent after this one would
            // have been about the new row anyway rather than the one the six
            // were opened over.
            // A row landing ends the wait the line was about. Asked again on
            // the next one, it is said again; left standing, it would be a
            // player being told something they worked out two turns ago.
            if (landed.length > 0) this.hintAsked = false;

            if (landed.length > 0) this.closeSay();

            if (landed.length > 0) this.showNewestRow();

            this.dismissExplanation(landed.length > 0);
            this.retireHint();
            this.openFirstExplanation();
        },

        /**
         * Runs the current through a guess that was waiting for the turn.
         *
         * Only when the row was already full: an empty row has nothing to light
         * up, and a player who has not pre-filled gets the ordinary unlit board
         * rather than a flourish about nothing.
         */
        surge() {
            if (typeof this.onSurge === 'function') this.onSurge();
        },

        /**
         * Brings the newest row into view.
         *
         * The rows scroll inside a box of their own, so a row landing past the
         * bottom of it is a row the player never sees - and the board is built
         * around the newest row sitting next to the palette that answers it.
         */
        showNewestRow() {
            if (typeof this.onNewRow === 'function') this.onNewRow();
        },

        /**
         * Sends the current back through the whole board, once, on the win.
         *
         * Only on the win arriving, never on a board that loads already won:
         * the celebration marks the moment the code went, and reloading is not
         * that moment - the same reason a played row does not strike twice.
         */
        celebrate() {
            if (typeof this.onCelebrate === 'function') this.onCelebrate();
        },

        /*
         * The clock, which only a daily board has anything to count. The page
         * drives it: this is where a board stops being a state machine and
         * starts depending on whether anybody is looking at it.
         */

        /** The day is in front of the player again, or was never away. */
        resumeClock() {
            if (this.gameOver) return;
            clock.run();
        },

        /** The page has gone. Bank what was counted and stop. */
        pauseClock() {
            clock.hold();
        },

        /** How long this board says it has been played for. */
        get elapsed() {
            return clock.elapsed();
        },

        /** Sends the current guess. The server is the only thing that can score it. */
        async submit() {
            // Alive on the opponent's turn so that this tap can be answered
            // rather than eaten by a disabled button - which is the one refusal
            // in the game a player earns by doing everything right.
            if (!this.ready) {
                if (this.charged) this.hintAsked = true;
                return;
            }

            this.submitting = true;
            this.error = '';
            const codes = [...this.guess];

            try {
                const doFetch = fetchImpl || globalThis.fetch;
                const res = await doFetch(this.guessUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ codes, ...timed() }),
                });

                if (!res.ok) {
                    this.fail(await readError(res));
                    return;
                }

                this.ingest(await res.json());
                // The row comes back holding its pins rather than empty. That
                // refill is the whole of what a pin does - but not on the row
                // that ended the game, which ingest has just emptied for good.
                if (this.canCompose) {
                    this.guess = [...this.locks];
                    this.settle();
                    this.active = this.firstEmpty;
                }
            } catch (err) {
                this.fail('Couldn\u2019t reach the server. Check your connection.');
            } finally {
                this.submitting = false;
            }
        },

        /**
         * Gives the board up, which the server settles by playing the fixed
         * give-up row.
         *
         * Only the daily offers this from the board. A game is given up from
         * the list on home, where the hold that asks lives - but both end at
         * the same endpoint, and the answer comes back in the same shape as a
         * guess, so it folds in the same way.
         */
        async giveUp() {
            if (this.gameOver || this.submitting || !this.forfeitUrl) return;

            this.submitting = true;
            this.error = '';
            try {
                const doFetch = fetchImpl || globalThis.fetch;
                const res = await doFetch(this.forfeitUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(timed()),
                });
                if (!res.ok) {
                    this.fail(await readError(res));
                    return;
                }
                this.ingest(await res.json());
            } catch (err) {
                this.fail('Couldn\u2019t reach the server. Check your connection.');
            } finally {
                this.submitting = false;
            }
        },

        /** Reports a refusal, in words and in the hand. */
        fail(message) {
            this.error = message;
            this.cue('refused');
        },

        /**
         * The buttons are server-rendered for any game with a second seat and
         * hidden here while it is empty, so a host who is sent a join over the
         * listener gets the lamp without reloading.
         */
        get canReact() {
            return this.reactions.length > 0 && this.rows.length > 0 && !this.awaitingOpponent;
        },

        /**
         * Puts the six out on the rule, or takes them back.
         *
         * The line they thread onto is the one saying who the board is waiting
         * for, so this is the only state the board has that silences that. It
         * is why every way out of it closes here rather than leaving a wire
         * hanging over a turn it is not describing.
         */
        toggleSay() {
            if (!this.canReact) return;
            this.saying = !this.saying;
            this.cue('peg');
        },

        /** Takes the wire back, from wherever the answer came. */
        closeSay() {
            this.saying = false;
        },

        /** Attaches a reaction to the most recent row. */
        async react(emoji) {
            if (!this.canReact || this.reacting) return;

            // Closed on the tap rather than on the answer: the send is the end
            // of the gesture, and a wire still standing while a request runs
            // reads as one that did not take it.
            this.closeSay();
            this.reacting = true;
            this.error = '';
            this.cue('peg');
            try {
                const doFetch = fetchImpl || globalThis.fetch;
                const res = await doFetch(`/g/${this.gameId}/react`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reaction: emoji }),
                });
                if (!res.ok) {
                    this.fail(await readError(res));
                    return;
                }
                this.ingest(await res.json());
            } catch (err) {
                this.fail('Couldn\u2019t reach the server. Check your connection.');
            } finally {
                this.reacting = false;
            }
        },

        /** The reactions on a row, as { mine, theirs }. */
        rowReactions(row) {
            return reactionsFor(this.chat, row, this.me);
        },

        rowHasReaction(row) {
            return hasReaction(this.chat, row, this.me);
        },

        /** One stored reaction string, as the faces to draw for it. */
        faces(reactions) {
            return faceList(reactions);
        },

        /** The file a face is drawn from, under the board's own faces path. */
        faceId(glyph) {
            return faceId(glyph);
        },

        /* What a score means, which is the one rule the board cannot show. */

        /** Whether this row's score is currently spelled out. */
        isExplaining(row) {
            const line = Number(row && row.line_number) || 0;
            return line !== 0 && line === this.explaining;
        },

        /**
         * This row's score in words, one line per number. Each line carries
         * both halves of the score as well, so it can be rendered led by the
         * whole score with only its own half lit.
         */
        explain(row) {
            return explainResult(row && row.result, this.matches);
        },

        /**
         * Whose guess an explanation is about, as the words that open it.
         *
         * The rows are already told apart by colour, which is the whole of what
         * says so until a player asks a row what it means. Asking is the moment
         * to say it in words too, and it costs the board nothing: the sentence
         * is one a player opened themselves.
         *
         * Nothing to say on a board with one player on it.
         */
        explainOwner(row) {
            if (this.solo || !row || !row.user) return '';
            if (row.user === this.me) return 'Your guess';
            return this.opponentName ? `${this.opponentName}\u2019s guess` : 'Their guess';
        },

        /**
         * What a screen reader hears instead of "two slash four", which is the
         * one place the colour and the lit digit carry nothing.
         */
        scoreLabel(row) {
            return this.explain(row)
                .map((part) => (part.note ? `${part.text}, ${part.note}` : part.text))
                .join('. ');
        },

        /**
         * A tap on a played row, which is what asks what its score means.
         *
         * The whole row answers, not only the number: the score is a small
         * target at the end of a wide row, and a player who has not tried it
         * has no reason to think the row says any more than it shows.
         */
        tapRow(row) {
            return this.toggleExplain(row);
        },

        /**
         * The same tap landing on the pegs, where a second one would mean
         * something else entirely - see tap.js.
         *
         * It waits there, and it stops there: the row above would otherwise
         * answer the same tap at once, which is the answer the waiting is for.
         */
        tapPegs(row, event) {
            // The press was held, and pinning a peg has already answered it.
            if (this.holdClick(event)) return false;

            if (event && event.stopPropagation) event.stopPropagation();
            tap.single(() => this.toggleExplain(row));
            return true;
        },

        /**
         * Opens this row's explanation, or shuts it if it was already open.
         *
         * Asking counts as learning: a player who has found the row tappable
         * on their own is not shown an explanation unprompted afterwards.
         */
        toggleExplain(row) {
            const line = Number(row && row.line_number) || 0;
            if (line === 0) return false;

            this.explaining = this.explaining === line ? 0 : line;
            this.autoExplained = false;
            this.rememberScoring();
            if (this.explaining !== 0 && typeof this.onExplain === 'function') this.onExplain(line);
            return true;
        },

        /**
         * The newest row that was actually somebody's guess.
         *
         * A forfeit row is scored like any other, but it was never a guess and
         * its pegs come from outside the alphabet on purpose. Spending the one
         * explanation a player is given unasked on that row would teach the
         * notation on a row that means something else entirely.
         */
        get lastGuessedRow() {
            for (let i = this.rows.length - 1; i >= 0; i--) {
                if (!this.rows[i].forfeit) return this.rows[i];
            }
            return null;
        },

        /**
         * Spells the newest score out unasked, once ever.
         *
         * A first-time player has no reason to suspect the number is tappable,
         * so the first score they are ever shown - their own or the opponent's,
         * whichever lands first - opens itself and says so. After that the board
         * stays quiet and the explanation is theirs to ask for.
         */
        openFirstExplanation() {
            if (this.scoringSeen) return false;

            const line = Number(this.lastGuessedRow && this.lastGuessedRow.line_number) || 0;
            if (line === 0) return false;

            this.explaining = line;
            this.autoExplained = true;
            this.rememberScoring();
            return true;
        },

        /**
         * Puts an explanation the player asked for away once a row lands.
         *
         * A row arriving is the board moving on, and the score being read
         * belongs to a row that is no longer the one in play. The player knows
         * how to get it back - asking is how it opened - so leaving it under a
         * newer row only pushes that row down the screen.
         *
         * The one still teaching is the exception, and the reason this is not
         * just closeExplanation: a player who has not yet been told rows are
         * tappable cannot get it back, so it is spared for as long as it is
         * saying so. Once that line has retired it is an ordinary open box and
         * leaves with the row after - see retireHint.
         */
        dismissExplanation(landed) {
            if (!landed || this.autoExplained) return false;
            return this.closeExplanation();
        },

        /** Puts the explanation away, however it came to be open. */
        closeExplanation() {
            if (this.explaining === 0) return false;

            this.explaining = 0;
            this.autoExplained = false;
            return true;
        },

        /**
         * Retires the line saying where explanations come from, once the board
         * has moved past the row that prompted it.
         *
         * The explanation itself stays until somebody closes it - it is what
         * the player is reading. This line is instruction, and instruction that
         * has been read has no business outliving the row it arrived on.
         */
        retireHint() {
            if (!this.autoExplained) return false;
            if (lastLineNumber(this.rows) === this.explaining) return false;

            this.autoExplained = false;
            return true;
        },

        rememberScoring() {
            if (this.scoringSeen) return;
            this.scoringSeen = true;
            markSeen(this.storage, SCORING_TUTORIAL);
        },

        /**
         * Whether the board still owes this player its opening lines.
         *
         * A row of their own is the whole answer. It is the record that they
         * have played, it comes from the server, and so it already survives a
         * reload, a closed tab and a different device - which is everything
         * anything kept locally would have been for. An empty board is then
         * just the case where nobody has a row yet, and the player who joins a
         * game already in progress is the case this exists for: the lines used
         * to leave with the host's first guess, taking the premise with them.
         *
         * A finished game is nobody's to play, so it is not told how.
         */
        get showPremise() {
            if (this.gameOver) return false;
            if (this.premiseLines.length === 0) return false;
            if (this.handedOver) return true;
            return this.myRowCount === 0;
        },

        /** How many rows this player has played. A forfeit is not one. */
        get myRowCount() {
            return this.rows.filter((row) => row.user === this.me && !row.forfeit).length;
        },

        /**
         * Whether this player has just made their first move and the board has
         * gone quiet on them for the first time.
         *
         * The one beat the opening lines are kept alive for. Everything else a
         * first-time player has to learn is on screen while they learn it; that
         * the board leaves and comes back is the only rule they meet by the
         * board doing nothing, which is also how a game that has stopped
         * working looks.
         */
        get handedOver() {
            if (this.solo || this.gameOver || this.myTurn) return false;
            return this.myRowCount === 1;
        },

        /**
         * Whether the offer of notifications is worth its whole card.
         *
         * It is, once, on the first wait of the game - the moment a player has
         * just learned the board takes turns and is deciding whether to sit
         * here or leave. From the next guess on the same offer is a footnote in
         * the same place, which says the same thing and costs no board.
         *
         * Counted from this player's own rows rather than remembered, for the
         * reason showPremise gives: a row is the server's record that they have
         * played, so it already survives a reload, a closed tab and a different
         * device. A flag in localStorage would do the same job worse, and it
         * would be the first thing stored that the game does not strictly need
         * - which is the exemption /privacy relies on.
         *
         * One row, not none: whose move it is alternates, so a player who is
         * waiting on a turn has by definition already played.
         */
        get notifyLoud() {
            return this.myRowCount <= 1;
        },

        /**
         * What that board says, as the lines it is stacked from.
         *
         * The game speaks for itself here rather than being described. It is
         * the only thing that can see the code, which makes it the only thing
         * with first-hand knowledge of what the player is up against.
         */
        get premiseLines() {
            // A page that has already said what this board is hands its own
            // lines, empty ones included. The daily's header is that page.
            if (Array.isArray(this.premise)) return this.premise;

            // Not the premise any more but the same space, because it answers
            // the question the premise was answering: the board has gone quiet,
            // and this says what the quiet is.
            if (this.handedOver) {
                const who = this.opponentName || 'Your opponent';
                return [`${who} is guessing. You\u2019re next.`];
            }

            const lines = ['I have a secret code.'];
            if (!this.solo) lines.push('You take turns guessing it.');

            // Whose move it is decides the shape of the sentence, because the
            // two states are not the same kind of thing to say: one is a report
            // about somebody else, the other is the only line here that has to
            // point at where the move is made.
            //
            // A host whose guest has not arrived still holds the turn, and the
            // share panel above already says the seat is empty. Being told they
            // are the one being waited on would talk them out of the move they
            // can make.
            //
            // Solo says only the half that points: the turn never leaves this
            // player, so naming whose it is contrasts against nothing.
            if (this.myTurn) {
                const move = this.solo ? 'Make your guess below.' : 'Your move — make your guess below.';
                return [...lines, move, 'Good luck 🍀'];
            }

            return [...lines, `${this.opponentName || 'Your opponent'} is guessing.`];
        },

        /**
         * Which of those lines is the one about whose move it is, so that the
         * view can give it air. -1 where no line here is about the turn.
         *
         * An index rather than a mark on the line itself: the lines are strings
         * and are handed to a page that supplies its own, and one of the two
         * shapes would have to be converted into the other on every read.
         */
        get premiseTurnIndex() {
            if (Array.isArray(this.premise) || this.handedOver) return -1;
            // Last on the opponent's turn, and second to last on this player's,
            // where the wish that closes the lines follows it.
            return this.myTurn ? this.premiseLines.length - 2 : this.premiseLines.length - 1;
        },

        /* The split palette: pick a colour, then a digit in it. */

        /** The distinct colours this mode's alphabet uses, in its own order. */
        get paletteColors() {
            const seen = [];
            for (const code of this.palette) {
                const { color } = parsePeg(code);
                if (color && !seen.includes(color)) seen.push(color);
            }
            return seen;
        },

        /** The ten pegs currently on offer, in the colour that is chosen. */
        get digitCodes() {
            return Array.from({ length: 10 }, (_, d) => `${this.paletteColor}${d}`);
        },

        /** Switches the digit row to another colour. Picks nothing by itself. */
        pickColor(color) {
            if (!this.paletteColors.includes(color)) return;
            this.paletteColor = color;
        },

        /**
         * Whether this row landed after the board was loaded, and so should
         * strike. Reloading a long game must not set every row flickering: the
         * strike marks an arrival, and these rows did not arrive.
         */
        isFreshRow(row) {
            return (Number(row && row.line_number) || 0) > this.loadedThrough;
        },

        get rowCountLabel() {
            const n = this.rows.length;
            return `${n} row${n === 1 ? '' : 's'}`;
        },

        /** The line number the next row will take. */
        get nextLineNumber() {
            return lastLineNumber(this.rows) + 1;
        },

        // --- template helpers -------------------------------------------------

        pegClass(code) {
            return pegClasses(code);
        },

        pegColor(code) {
            return parsePeg(code).color;
        },

        pegDigit(code) {
            return parsePeg(code).digit;
        },

        /**
         * The guess row, as the sockets it is made of.
         *
         * The row is drawn from these and every binding on a socket then reads
         * the board for itself. Drawing it from the pegs instead hands each
         * socket a copy of what it holds, and a copy can be a beat behind the
         * board that changed it: a pin fills a socket and marks it in one go,
         * and the socket drew the mark against the peg it held before.
         */
        slots: Array.from({ length: CODE_LENGTH }, (_, i) => i),

        /**
         * The classes on a socket in the guess row: its peg, and then whichever
         * of the two states it is in - both, where the cursor is sitting on a
         * pinned socket, which a pin does nothing to prevent. The rim is a note
         * about the next guess and the pulse is where the next peg lands: two
         * different things to say, and a socket wearing one still has to be
         * able to say the other. The week strip pairs them the same way, on a
         * today that is also the day being shown.
         *
         * Takes the socket rather than the peg, like everything else the row
         * draws, so what it reads is the board at the moment it runs. See slots.
         *
         * Spelled out, not assembled: Tailwind drops any class it cannot see
         * written down literally.
         */
        slotClass(index) {
            const classes = [pegClasses(this.guess[index])];
            if (this.isPinned(index)) classes.push('peg--pinned');
            if (index === this.active && this.canCompose) classes.push('peg--active');
            return classes.join(' ');
        },

        /**
         * What a screen reader hears on a socket, since the pin is a ring of
         * light and carries nothing to anybody not looking at it.
         */
        slotLabel(index) {
            const code = this.guess[index];
            const name = parsePeg(code).empty ? 'empty socket' : `peg ${code}`;
            return this.isPinned(index) ? `${name}, pinned` : name;
        },

        result(row) {
            return formatResult(row && row.result);
        },

        /** 'on' for your rows, 'theirs' for the opponent's, 'gold' for a cracked code. */
        mode(row) {
            return rowMode(row, this.me, CODE_LENGTH);
        },

        /**
         * The classes on the box around a row: its accent always, and the tint
         * only while its score is being spelled out. The tint is what says the
         * pegs, the number and the words below them are one thing.
         */
        groupClass(row) {
            const accent = GROUP_CLASSES[this.mode(row)] || GROUP_CLASSES.on;
            return this.isExplaining(row) ? `${accent} peg-group--lit` : accent;
        },
    };

    // A restored row puts the cursor on whatever it still wants, which is
    // where the player left off. Reusing the getter rather than working it out
    // above, so there is one answer to what the next empty socket is.
    if (draft) board.active = board.firstEmpty;

    // A board that loads with rows already on it - a reload, or a guest joining
    // a game the host has started - has a score to explain before anything is
    // played, so this cannot wait for the first ingest.
    board.openFirstExplanation();

    return board;
}

/**
 * Everything the opponent is currently showing, as one comparable string.
 *
 * Not a count: a reaction is rewritten in place as it grows, and once it is at
 * its cap a new one pushes the oldest off, so the number of them can stay
 * exactly the same across the update that carried a new one.
 */
function theirReactions(chat, me) {
    const parts = [];
    for (const [line, entry] of Object.entries(chat || {})) {
        if (!entry || typeof entry !== 'object') continue;
        for (const [uid, value] of Object.entries(entry)) {
            if (uid !== me && typeof value === 'string' && value !== '') {
                parts.push(`${line}:${uid}:${value}`);
            }
        }
    }
    return parts.sort().join('|');
}

async function readError(res) {
    try {
        const body = await res.json();
        if (body && typeof body.error === 'string') return body.error;
    } catch {
        // Fall through to the generic message below.
    }
    return 'That guess wasn\u2019t accepted.';
}
